import { useMemo, useCallback, useEffect } from 'react';
import {
  Typography,
  Checkbox,
  Button,
  FormControlLabel,
} from '@mui/material';
import { Unit, Lease, ActionItem } from '@urbanmix-tech/shared-js';
import { convertPseudoDateToRealDate } from '@/helpers/common';
import { useDispatch } from 'react-redux';
import {
  setBuildingVisual,
  setHighlightedUnits,
} from '@/features/project/projectSlice';
import { ColorGradeVisualizer } from '@urbanmix-tech/um-embedded';
import {
  ExpiringLeasesAggregation,
  AggregatedData,
  UnitData,
} from '@/components/BuildingTabs/ExpiringLeasesAggrigation';
import { LeaseCard } from '@/components/BuildingTabs/LeaseCard';
import { CategoryGradientVisualizer, colorScales } from '@/components/BuildingTabs/BuildingVisualizers';
import { PricingWizardContextValue } from '../types';

interface UnitSelectionStepProps {
  units: Unit[];
  leases: Lease[];
  actionItems: ActionItem[];
  wizard: PricingWizardContextValue;
}

// Aggregator function to group units by expiration days
const aggregator = (units: Unit[], categories: number[]): AggregatedData => {
  const data = units.map((unit: Unit) => {
    let endDate = convertPseudoDateToRealDate(unit.leaseEndDate) ?? new Date();
    return {
      daysToExpire: Math.ceil(
        (endDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
      ),
      unit,
      type: unit.affordable ? 'affordable' : 'market',
    };
  });

  let groups: Record<string, { units: UnitData[]; category: number }> = {};
  let keys = categories.map((days) => `${days}`);
  keys.push('rest');

  data.forEach((unitData: UnitData) => {
    let category =
      categories.find((days) => unitData.daysToExpire <= days) ?? categories[0];
    let key = category ? `${category}` : 'rest';

    if (!groups[key]) {
      groups[key] = { units: [], category };
    }
    groups[key]!.units.push(unitData);
  });

  return { grouped: groups, categories, keys };
};

export function UnitSelectionStep({
  units,
  leases,
  actionItems,
  wizard,
}: UnitSelectionStepProps) {
  const dispatch = useDispatch();
  const { state, actions, selectedUnits, totalSelectedCount } = wizard;
  const { filters } = state;

  const categories = [0, 20, 30, 40, 60, 90, 120];

  // Create lease map for quick lookup
  const leaseMap = useMemo(() => {
    const map = new Map<string, Lease>();
    leases.forEach((lease) => {
      const existing = map.get(lease.unitId);
      if (!existing || new Date(lease.leaseStartDate) > new Date(existing.leaseStartDate)) {
        map.set(lease.unitId, lease);
      }
    });
    return map;
  }, [leases]);

  // Filter units based on filters
  const filteredUnits = useMemo(() => {
    return units.filter((unit) => {
      let endDate = convertPseudoDateToRealDate(unit.leaseEndDate);
      if (endDate) {
        let today = new Date();
        let diff = Math.ceil(
          (endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
        );
        if (diff <= filters.expiresInDays && diff >= 0) {
          if (unit.affordable && filters.showAffordable) {
            return true;
          } else if (!unit.affordable && filters.showMarket) {
            return true;
          }
        }
      }
      return false;
    });
  }, [units, filters]);

  // Aggregate data for the summary table
  const aggregatedData = useMemo(() => {
    return aggregator(filteredUnits, categories);
  }, [filteredUnits]);

  // Get units for display based on selected category keys
  const displayUnits = useMemo(() => {
    if (filters.selectedCategoryKeys.length === 0) {
      return filteredUnits;
    }

    let unitsData: UnitData[] = [];
    filters.selectedCategoryKeys.forEach((key: string) => {
      if (aggregatedData.grouped[key]) {
        unitsData = unitsData.concat(aggregatedData.grouped[key].units);
      }
    });

    return unitsData
      .map((ud) => ud.unit)
      .sort((a, b) => {
        const endA = convertPseudoDateToRealDate(a.leaseEndDate)?.getTime() ?? 0;
        const endB = convertPseudoDateToRealDate(b.leaseEndDate)?.getTime() ?? 0;
        return endA - endB;
      });
  }, [filteredUnits, filters.selectedCategoryKeys, aggregatedData]);

  // Set up building visualization
  const visualizer = useMemo(() => {
    let marketDays = new ColorGradeVisualizer(colorScales.market, 10, 120);
    let affordableDays = new ColorGradeVisualizer(colorScales.affordable, 10, 120);

    return new CategoryGradientVisualizer({
      affordable: affordableDays,
      market: marketDays,
    });
  }, []);

  useEffect(() => {
    const data = displayUnits.map((unit) => {
      const endDate = convertPseudoDateToRealDate(unit.leaseEndDate);
      const days = endDate
        ? Math.ceil((endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
        : 0;
      return {
        apartmentId: unit.id,
        value: (unit.affordable ? 'affordable' : 'market') + '_' + days,
        label: 'Type',
      };
    });

    dispatch(
      setBuildingVisual({
        apartmentData: data,
        visualizer: visualizer,
      })
    );

    setTimeout(() => {
      dispatch(setHighlightedUnits(data));
    }, 500);
  }, [displayUnits, visualizer, dispatch]);

  const handleCategoriesChange = useCallback(
    (keys: string[]) => {
      actions.setFilters({ selectedCategoryKeys: keys });
    },
    [actions]
  );

  const handleToggleUnit = useCallback(
    (unitId: string) => {
      actions.toggleUnitSelection(unitId);
    },
    [actions]
  );

  const handleSelectAll = useCallback(() => {
    actions.selectAllUnits(displayUnits.map((u) => u.id));
  }, [actions, displayUnits]);

  const handleClearSelection = useCallback(() => {
    actions.clearUnitSelection();
  }, [actions]);

  // Calculate days to expire for a unit
  const getDaysToExpire = (unit: Unit) => {
    const endDate = convertPseudoDateToRealDate(unit.leaseEndDate);
    if (!endDate) return Infinity;
    return Math.ceil((endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  };

  return (
    <div className="unit-selection-step h-full flex flex-col px-8 py-4">
      {/* Header with selection info */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <Typography variant="h6">Select Units to Reprice</Typography>
          <Typography variant="body2" color="textSecondary">
            Filter by expiration timeframe and select units for the pricing wizard
          </Typography>
        </div>
        <div className="flex items-center gap-4">
          <Typography variant="body1">
            <strong>{totalSelectedCount}</strong> units selected
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={handleSelectAll}
            disabled={displayUnits.length === 0}
          >
            Select All ({displayUnits.length})
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={handleClearSelection}
            disabled={totalSelectedCount === 0}
          >
            Clear
          </Button>
        </div>
      </div>

      {/* Aggregation table */}
      <div className="mb-4">
        <ExpiringLeasesAggregation
          tableData={aggregatedData}
          selectedCategoryKeys={filters.selectedCategoryKeys}
          onCategoriesSelected={handleCategoriesChange}
          showMarket={filters.showMarket}
          onShowMarketChange={(value) => actions.setFilters({ showMarket: value })}
          showAffordable={filters.showAffordable}
          onShowAffordableChange={(value) => actions.setFilters({ showAffordable: value })}
        />
      </div>

      {/* Unit list with checkboxes */}
      {filters.selectedCategoryKeys.length > 0 && (
        <div className="flex-1 overflow-auto">
          <div className="flex flex-col gap-4">
            {displayUnits.map((unit) => {
              const isSelected = state.selectedUnitIds.has(unit.id);
              const daysToExpire = getDaysToExpire(unit);
              const unitLease = leaseMap.get(unit.id);

              return (
                <div
                  key={unit.id}
                  className={`flex items-start gap-4 p-2 rounded border ${
                    isSelected
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-zinc-700 hover:border-zinc-500'
                  }`}
                >
                  <Checkbox
                    checked={isSelected}
                    onChange={() => handleToggleUnit(unit.id)}
                    sx={{ mt: 1 }}
                  />
                  <div className="flex-1">
                    <LeaseCard
                      unit={unit}
                      lease={
                        unitLease || ({
                          id: `unit-${unit.id}`,
                          projectId: unit.projectId || '',
                          buildingId: unit.buildingId || 0,
                          unitId: unit.id,
                          leaseStartDate: typeof unit.leaseStartDate === 'string' ? unit.leaseStartDate : '',
                          leaseEndDate: typeof unit.leaseEndDate === 'string' ? unit.leaseEndDate : '',
                          monthlyRent: unit.actualRent || 0,
                          securityDeposit: 0,
                          concessions: [],
                          totalConcessionAmount: 0,
                          status: 'valid' as const,
                          eLeaseFields: {},
                          createdAt: new Date(),
                          updatedAt: new Date(),
                        } as any)
                      }
                      processes={[]}
                      compact={true}
                      showActions={false}
                      borderColorOverride={
                        visualizer?.getColor
                          ? visualizer.getColor(
                              (unit.affordable ? 'affordable' : 'market') +
                                '_' +
                                daysToExpire
                            )
                          : undefined
                      }
                      daysToExpire={daysToExpire}
                      expirationDate={convertPseudoDateToRealDate(unit.leaseEndDate) ?? undefined}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {filters.selectedCategoryKeys.length === 0 && (
        <div className="flex-1 flex items-center justify-center">
          <Typography variant="body1" color="textSecondary">
            Select one or more expiration categories above to view units
          </Typography>
        </div>
      )}
    </div>
  );
}
